import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  InputGroup,
  InputGroupText,
  Badge,
} from "reactstrap";
import { GetData, PostData, DeleteData } from "../../services/api";
import Swal from "sweetalert2";

const toSlug = (str) =>
  (str || "").toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_-]/g, "").slice(0, 40) || "mi_lista";

const IntegrationModal = ({ isOpen, toggle, listId, listName }) => {
  const [keyId, setKeyId]           = useState(null);
  const [apiKey, setApiKey]         = useState(null);
  const [loadingKey, setLoadingKey] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [revoking, setRevoking]     = useState(false);
  const [revealed, setRevealed]     = useState(false);
  const [codeLang, setCodeLang]     = useState("curl");
  const [createdBy, setCreatedBy]   = useState("");

  const apiBase  = process.env.REACT_APP_API || "http://127.0.0.1:5001";
  const endpoint = `${apiBase}/integration/contacts`;
  const KEY      = apiKey || "TU_API_KEY";

  const fetchKey = useCallback(async () => {
    if (!listId) return;
    setLoadingKey(true);
    try {
      const res = await GetData(`/integration/api-keys/${listId}`);
      setApiKey(res?.data?.apiKey || null);
      setKeyId(res?.data?.keyId || null);
    } catch (_) { setApiKey(null); setKeyId(null); }
    finally     { setLoadingKey(false); }
  }, [listId]);

  useEffect(() => {
    if (isOpen) {
      setRevealed(false);
      setCreatedBy(toSlug(listName));
      fetchKey();
    }
  }, [isOpen, listName, fetchKey]);

  const handleGenerate = async () => {
    const isRegen = !!apiKey;
    const ok = await Swal.fire({
      title: isRegen ? "¿Regenerar API Key?" : "Generar API Key",
      text: isRegen
        ? "La clave anterior dejará de funcionar de inmediato."
        : "Se generará una clave para esta lista.",
      icon: "warning", showCancelButton: true,
      confirmButtonColor: "#6C63FF", cancelButtonColor: "#6c757d",
      confirmButtonText: isRegen ? "Sí, regenerar" : "Generar",
      cancelButtonText: "Cancelar",
    });
    if (!ok.isConfirmed) return;
    setGenerating(true);
    try {
      const res = await PostData(`/integration/api-keys/${listId}`, {});
      setApiKey(res?.data?.apiKey || null);
      setKeyId(res?.data?.keyId || null);
      setRevealed(true);
    } catch (_) {
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo generar la API Key" });
    } finally { setGenerating(false); }
  };

  const handleRevoke = async () => {
    const ok = await Swal.fire({
      title: "¿Revocar API Key?", text: "Esta lista quedará sin integración activa.",
      icon: "warning", showCancelButton: true,
      confirmButtonColor: "#E9553E", cancelButtonColor: "#6c757d",
      confirmButtonText: "Sí, revocar", cancelButtonText: "Cancelar",
    });
    if (!ok.isConfirmed) return;
    setRevoking(true);
    try {
      await DeleteData(`/integration/api-keys/${keyId}`);
      setApiKey(null); setKeyId(null); setRevealed(false);
      Swal.fire({ icon: "success", title: "Revocada", timer: 1800, showConfirmButton: false });
    } catch (_) {
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo revocar" });
    } finally { setRevoking(false); }
  };

  const copy = (text) =>
    navigator.clipboard.writeText(text).then(() =>
      Swal.fire({ icon: "success", title: "¡Copiado!", timer: 900, showConfirmButton: false })
    );

  const masked = (k) => k.slice(0, 8) + "••••••••••••••••" + k.slice(-4);

  const bodyObj = {
    name: "Juan",
    lastName: "García",
    email: "juan@ejemplo.com",
    phone: "+52 55 1234 5678",
    createdBy: createdBy || "mi_app",
    url: "https://mi-sitio.com/registro",
    referencia: "REF-12345",
    tags: ["webinar", "interesado"],
    attributes: { utm_source: "facebook" },
  };
  const bodyStr = JSON.stringify(bodyObj, null, 2);

  const examples = {
    curl:
`curl -X POST "${endpoint}" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${KEY}" \\
  -d '${bodyStr}'`,
    js:
`const res = await fetch("${endpoint}", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "X-API-Key": "${KEY}"
  },
  body: JSON.stringify(${bodyStr})
});
const data = await res.json();
// { contactId, isNew, addedToList, message }`,
    python:
`import requests

r = requests.post(
  "${endpoint}",
  headers={"X-API-Key": "${KEY}"},
  json=${bodyStr}
)
print(r.json())
# { contactId, isNew, addedToList, message }`,
    php:
`<?php
$ch = curl_init("${endpoint}");
curl_setopt_array($ch, [
  CURLOPT_RETURNTRANSFER => true,
  CURLOPT_POST           => true,
  CURLOPT_HTTPHEADER     => [
    "Content-Type: application/json",
    "X-API-Key: ${KEY}",
  ],
  CURLOPT_POSTFIELDS => json_encode([
    "name"       => "Juan",
    "lastName"   => "García",
    "email"      => "juan@ejemplo.com",
    "phone"      => "+52 55 1234 5678",
    "createdBy"  => "${createdBy || "mi_app"}",
    "url"        => "https://mi-sitio.com/registro",
    "referencia" => "REF-12345",
    "tags"       => ["webinar", "interesado"],
    "attributes" => ["utm_source" => "facebook"],
  ]),
]);
$data = json_decode(curl_exec($ch), true);`,
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" scrollable>
      <ModalHeader toggle={toggle}>
        <i className="mdi mdi-api me-2" />Integración API — {listName}
      </ModalHeader>
      <ModalBody>

        {/* Endpoint */}
        <div className="mb-4">
          <label className="form-label" style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#6c757d" }}>
            Endpoint
          </label>
          <InputGroup>
            <InputGroupText style={{ fontFamily: "monospace", fontSize: 12, background: "#e9ecef" }}>POST</InputGroupText>
            <Input readOnly value={endpoint} style={{ fontFamily: "monospace", fontSize: 13 }} />
            <Button color="secondary" outline size="sm" onClick={() => copy(endpoint)}>
              <i className="mdi mdi-content-copy" />
            </Button>
          </InputGroup>
          <small className="text-muted">
            Autenticación via <code>X-API-Key</code>. La lista destino está codificada en la clave — no se envía en el body.
          </small>
        </div>

        {/* API Key management */}
        <div className="mb-4">
          <label className="form-label" style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#6c757d" }}>
            API Key de esta lista <span style={{ fontWeight: 400 }}>(header: X-API-Key)</span>
          </label>

          {loadingKey ? (
            <div className="text-muted d-flex align-items-center gap-2" style={{ fontSize: 13 }}>
              <span className="spinner-border spinner-border-sm" style={{ width: "0.75rem", height: "0.75rem" }} />
              Verificando...
            </div>
          ) : apiKey ? (
            <>
              <InputGroup>
                <Input
                  readOnly
                  value={revealed ? apiKey : masked(apiKey)}
                  style={{ fontFamily: "monospace", fontSize: 13 }}
                />
                <Button color="secondary" outline size="sm" onClick={() => setRevealed(v => !v)} title={revealed ? "Ocultar" : "Mostrar"}>
                  <i className={`mdi ${revealed ? "mdi-eye-off" : "mdi-eye"}`} />
                </Button>
                <Button color="secondary" outline size="sm" onClick={() => copy(apiKey)} title="Copiar">
                  <i className="mdi mdi-content-copy" />
                </Button>
                <Button color="warning" outline size="sm" onClick={handleGenerate} disabled={generating} title="Regenerar (desactiva la clave actual)">
                  <i className="mdi mdi-refresh" />
                </Button>
                <Button color="danger" outline size="sm" onClick={handleRevoke} disabled={revoking} title="Revocar">
                  <i className="mdi mdi-delete-outline" />
                </Button>
              </InputGroup>
              <small className="text-muted">
                Esta clave es exclusiva de <strong>{listName}</strong>. Regenerar desactiva la clave anterior.
              </small>
            </>
          ) : (
            <div className="d-flex align-items-center gap-3">
              <span className="text-muted" style={{ fontSize: 13 }}>No hay API Key generada para esta lista</span>
              <Button color="primary" size="sm" onClick={handleGenerate} disabled={generating}>
                {generating
                  ? <><span className="spinner-border spinner-border-sm me-1" style={{ width: "0.7rem", height: "0.7rem" }} />Generando...</>
                  : <><i className="mdi mdi-key-plus me-1" />Generar API Key</>}
              </Button>
            </div>
          )}
        </div>

        {/* createdBy field */}
        <div className="mb-4">
          <label className="form-label" style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#6c757d" }}>
            createdBy <span style={{ fontWeight: 400 }}>(identifica la fuente — se actualiza en los ejemplos)</span>
          </label>
          <InputGroup>
            <Input
              value={createdBy}
              onChange={e => setCreatedBy(e.target.value)}
              placeholder="ej: mi_app, shopify, tureinicio-3"
              style={{ fontFamily: "monospace", fontSize: 13 }}
            />
            <Button color="secondary" outline size="sm" onClick={() => copy(createdBy)} disabled={!createdBy} title="Copiar">
              <i className="mdi mdi-content-copy" />
            </Button>
          </InputGroup>
          <small className="text-muted">Pre-llenado con el nombre de la lista. Edítalo para identificar tu integración.</small>
        </div>

        {/* Code examples */}
        <div className="mb-3">
          <div className="d-flex gap-2 mb-2">
            {["curl", "js", "python", "php"].map(lang => (
              <Button key={lang}
                color={codeLang === lang ? "primary" : "secondary"}
                outline={codeLang !== lang} size="sm"
                onClick={() => setCodeLang(lang)}>
                {lang === "js" ? "JavaScript" : lang === "curl" ? "cURL" : lang.charAt(0).toUpperCase() + lang.slice(1)}
              </Button>
            ))}
          </div>
          <div className="position-relative rounded" style={{ background: "#0d0d1a" }}>
            <pre style={{ padding: "16px 48px 16px 16px", margin: 0, fontFamily: "monospace", fontSize: 12, color: "#a8b5d8", overflowX: "auto", whiteSpace: "pre", maxHeight: 360 }}>
              {examples[codeLang]}
            </pre>
            <Button color="light" size="sm"
              style={{ position: "absolute", top: 8, right: 8, fontSize: 11 }}
              onClick={() => copy(examples[codeLang])}>
              <i className="mdi mdi-content-copy me-1" />Copiar
            </Button>
          </div>
        </div>

        {/* Response */}
        <div className="p-3 rounded mb-3" style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", fontSize: 12 }}>
          <strong style={{ color: "#166534" }}>Respuesta 200:</strong>
          <code style={{ color: "#166534", display: "block", marginTop: 4 }}>
            {`{ "contactId": "abc123", "isNew": true, "addedToList": true, "message": "..." }`}
          </code>
        </div>

        {/* Field reference */}
        <table className="table table-sm table-bordered" style={{ fontSize: 12 }}>
          <thead style={{ background: "#f8f9fa" }}>
            <tr>
              <th style={{ width: 140 }}>Campo body</th>
              <th style={{ width: 75 }}>Tipo</th>
              <th style={{ width: 85 }}>Requerido</th>
              <th>Descripción</th>
            </tr>
          </thead>
          <tbody>
            {[
              { f: "email",      t: "string", r: true,  d: "Clave de deduplicación. Si el email ya existe el contacto se actualiza." },
              { f: "name",       t: "string", r: true,  d: "Nombre(s)." },
              { f: "lastName",   t: "string", r: true,  d: "Apellido(s)." },
              { f: "phone",      t: "string", r: true,  d: "Teléfono con código de país." },
              { f: "createdBy",  t: "string", r: false, d: "Fuente de la integración. Si se omite: \"api_integration\"." },
              { f: "url",        t: "string", r: false, d: "URL de la página de origen." },
              { f: "referencia", t: "string", r: false, d: "Código interno. Viaja en eventos pero NO se persiste en BD." },
              { f: "tags",       t: "array",  r: false, d: "Etiquetas. Se fusionan si el contacto ya existe." },
              { f: "attributes", t: "object", r: false, d: "Campos custom clave-valor (UTMs, etc.). Se fusionan." },
            ].map(({ f, t, r, d }) => (
              <tr key={f}>
                <td><code style={{ fontSize: 11 }}>{f}</code></td>
                <td style={{ color: "#6c757d" }}>{t}</td>
                <td style={{ textAlign: "center" }}>
                  {r ? <Badge color="danger" style={{ fontSize: 10 }}>Sí</Badge>
                     : <Badge color="light" style={{ fontSize: 10, color: "#6c757d", border: "1px solid #dee2e6" }}>No</Badge>}
                </td>
                <td style={{ color: "#444" }}>{d}</td>
              </tr>
            ))}
          </tbody>
        </table>

      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={toggle}>Cerrar</Button>
      </ModalFooter>
    </Modal>
  );
};

export default IntegrationModal;
